import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { api } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'
import AddMealLibraryModal from '../components/AddMealLibraryModal'

const MEAL_ICONS = { manual: '🍳', imported: '🌐', restaurant: '🥡' }
const MEAL_LABELS = { manual: 'Home recipe', imported: 'Imported recipe', restaurant: 'Restaurant' }

export default function MealsPage() {
  const { user } = useAuth()
  const [meals, setMeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [addingMeal, setAddingMeal] = useState(false)
  const [expandedMeal, setExpandedMeal] = useState(null)
  const [editingMeal, setEditingMeal] = useState(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editLoading, setEditLoading] = useState(false)

  const isReadOnly = user?.role === 'readonly'

  useEffect(() => { loadMeals() }, [])

  async function loadMeals() {
    try {
      const result = await api.listMeals()
      setMeals(result.meals)
    } catch (err) {
      toast.error('Failed to load meals')
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete(mealId) {
    if (!confirm('Remove this meal from your library?')) return
    try {
      await api.deleteMeal(mealId)
      setMeals(prev => prev.filter(m => m.mealId !== mealId))
      toast.success('Removed')
    } catch (err) {
      toast.error(err.message)
    }
  }

  function startEdit(meal) {
    setEditingMeal(meal)
    setEditName(meal.name || '')
    setEditDescription(meal.description || '')
    setEditNotes(meal.notes || '')
  }

  async function handleSaveEdit(e) {
    e.preventDefault()
    if (!editName.trim()) return
    setEditLoading(true)
    try {
      const result = await api.updateMeal(editingMeal.mealId, {
        name: editName.trim(),
        description: editDescription.trim() || null,
        notes: editNotes.trim() || null,
      })
      setMeals(prev => prev.map(m => m.mealId === editingMeal.mealId ? result.meal : m))
      setEditingMeal(null)
      toast.success('Meal updated!')
    } catch (err) {
      toast.error(err.message)
    } finally {
      setEditLoading(false)
    }
  }

  const filtered = meals.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = filter === 'all' || m.type === filter
    return matchesSearch && matchesFilter
  })

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Meal Library</h2>
        {!isReadOnly && (
          <button onClick={() => setAddingMeal(true)} className="btn-primary px-3 py-1.5 text-sm">+ Add meal</button>
        )}
      </div>

      <input type="search" placeholder="Search meals…" value={search} onChange={e => setSearch(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 mb-3 focus:outline-none focus:ring-2 focus:ring-green-500" />

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {[
          { id: 'all', label: 'All' },
          { id: 'manual', label: '🍳 Home' },
          { id: 'imported', label: '🌐 Imported' },
          { id: 'restaurant', label: '🥡 Restaurants' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setFilter(tab.id)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === tab.id ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-gray-400 mb-3">{filtered.length} meal{filtered.length !== 1 ? 's' : ''}</p>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-400">{search ? `No meals match "${search}"` : 'No meals yet. Add your first one!'}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map(meal => (
            <div key={meal.mealId} className="card">
              <button className="w-full text-left p-4" onClick={() => setExpandedMeal(expandedMeal === meal.mealId ? null : meal.mealId)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl shrink-0">{MEAL_ICONS[meal.type]}</span>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{meal.name}</p>
                      <p className="text-xs text-gray-400">{MEAL_LABELS[meal.type]}</p>
                    </div>
                  </div>
                  <span className="text-gray-300 ml-2">{expandedMeal === meal.mealId ? '▲' : '▼'}</span>
                </div>
              </button>

              {expandedMeal === meal.mealId && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  {meal.type === 'manual' && (
                    <>
                      {meal.description && <p className="text-sm text-gray-600 mb-2">{meal.description}</p>}
                      {meal.notes && <p className="text-sm text-gray-500">{meal.notes}</p>}
                    </>
                  )}
                  {meal.type === 'imported' && (
                    <>
                      {meal.summary && <p className="text-sm text-gray-600 mb-2">{meal.summary}</p>}
                      <div className="flex gap-3 text-xs text-gray-400 mb-2">
                        {meal.prepTime && <span>⏱️ {meal.prepTime}</span>}
                        {meal.servings && <span>👥 {meal.servings}</span>}
                      </div>
                      {meal.sourceUrl && (
                        <a href={meal.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:text-green-700">View recipe ↗</a>
                      )}
                    </>
                  )}
                  {meal.type === 'restaurant' && (
                    <>
                      {meal.address && <p className="text-sm text-gray-600 mb-1">📍 {meal.address}</p>}
                      {meal.mapsUrl && (
                        <a href={meal.mapsUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:text-green-700">Open in Maps ↗</a>
                      )}
                    </>
                  )}
                  {!isReadOnly && (
                    <div className="mt-3 flex gap-4">
                      {meal.type === 'manual' && (
                        <button onClick={() => startEdit(meal)} className="text-xs text-green-600 hover:text-green-700">Edit</button>
                      )}
                      <button onClick={() => handleDelete(meal.mealId)} className="text-xs text-red-400 hover:text-red-600">Remove from library</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {addingMeal && (
        <AddMealLibraryModal
          onClose={() => setAddingMeal(false)}
          onAdded={(meal) => {
            setMeals(prev => [...prev, meal].sort((a, b) => a.name.localeCompare(b.name)))
            setAddingMeal(false)
            toast.success(`"${meal.name}" added to library`)
          }}
        />
      )}

      {editingMeal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setEditingMeal(null)} />
          <div className="fixed top-4 left-0 right-0 max-w-2xl mx-auto bg-white rounded-2xl z-50 max-h-[92vh] flex flex-col">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-4 pb-3 flex items-center justify-between border-b border-gray-100">
              <p className="font-semibold text-gray-900">Edit meal</p>
              <button onClick={() => setEditingMeal(null)} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
            </div>
            <form onSubmit={handleSaveEdit} className="p-4 flex flex-col gap-3 overflow-y-auto">
              <div>
                <label className="text-sm font-medium text-gray-700">Meal name *</label>
                <input type="text" value={editName} onChange={e => setEditName(e.target.value)} required autoFocus
                  className="w-full mt-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description (optional)</label>
                <input type="text" value={editDescription} onChange={e => setEditDescription(e.target.value)}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
              </div>
              <button type="submit" disabled={editLoading || !editName.trim()} className="btn-primary w-full py-3 text-base">
                {editLoading ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  )
}
